import { useEffect, useRef } from 'react'
import classNames from 'classnames'

import { getQaSelector } from '../../utils/common'
import {
  AITextGeneration,
  AITextGenerationDraftPanel,
} from '../AITextGeneration'

import { QA_NAME_AI_TEXT_GENERATION_FIELD } from './AITextGenerationField.constants'
import { useAITextGenerationFieldState } from './AITextGenerationField.hooks'
import { isAITextGenerationFieldModeDraft } from './AITextGenerationField.utils'

import * as styles from './AITextGenerationField.module.css'

function AITextGenerationField({
  children,
  className = styles.AITextGenerationField,
  disabled,
  fieldContent,
  fieldName,
  hasExistingContent,
  isError,
  localContext,
  subjectFieldName,
  type,
  onApplyText,
  onModeChange,
}) {
  const regenerateRef = useRef(null)

  const {
    draftSubject,
    draftText,
    handleApply,
    handleCopy,
    handleDiscard,
    handleGenerate,
    handleGenerateStart,
    handleGenerateStop,
    handleInsertBelow,
    handleRegenerate,
    handleReplace,
    isCopied,
    isGenerating,
    mode,
    promptText,
    showAiNotice,
  } = useAITextGenerationFieldState({
    fieldName,
    regenerateRef,
    subjectFieldName,
    onApplyText,
  })

  const isDraftMode = isAITextGenerationFieldModeDraft(mode)

  const onModeChangeRef = useRef(onModeChange)

  useEffect(() => {
    onModeChangeRef.current = onModeChange
  }, [onModeChange])

  useEffect(() => {
    onModeChangeRef.current?.(isDraftMode)

    return () => {
      onModeChangeRef.current?.(false)
    }
  }, [isDraftMode])

  const qaProps = getQaSelector({
    value: QA_NAME_AI_TEXT_GENERATION_FIELD,
  })

  return (
    <div {...qaProps} className={className}>
      <div className={styles.fieldArea}>
        {isDraftMode && !isGenerating ? (
          <AITextGenerationDraftPanel
            draftSubject={draftSubject}
            generatedText={draftText}
            hasExistingContent={hasExistingContent}
            isCopied={isCopied}
            isError={isError}
            promptText={promptText}
            showAiNotice={showAiNotice}
            onApply={handleApply}
            onCopy={handleCopy}
            onDiscard={handleDiscard}
            onInsertBelow={handleInsertBelow}
            onRegenerate={handleRegenerate}
            onReplace={handleReplace}
          />
        ) : null}

        <div
          className={classNames(styles.fieldWrapper, {
            [styles.fieldWrapperHidden]: isDraftMode && !isGenerating,
            [styles.fieldWrapperLoading]: isDraftMode && isGenerating,
          })}
        >
          {isDraftMode && isGenerating ? (
            <div className={styles.loadingOverlay}>
              <AITextGenerationDraftPanel isLoading />
            </div>
          ) : null}
          {children}
        </div>
      </div>

      <AITextGeneration
        className={styles.generationForm}
        disabled={disabled}
        fieldContent={fieldContent}
        localContext={localContext}
        regenerateRef={regenerateRef}
        type={type}
        onGenerate={handleGenerate}
        onGenerateStart={handleGenerateStart}
        onGenerateStop={handleGenerateStop}
      />
    </div>
  )
}

export default AITextGenerationField
